/**
 * AppliedFiltersBar — generic, removable summary of active column filters.
 *
 * Reads TanStack `columnFilters` plus a map of `ColumnFilterSpec` keyed by columnId
 * and renders one pill per active filter (e.g. "Skills: React, Python ×"). Each pill's
 * × clears that one column; a trailing "Clear all" clears everything. Renders nothing
 * when no filters are active. Fully domain-agnostic.
 */
import React, { useMemo } from "react";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { X } from "lucide-react";
import { isColumnFilterEmpty } from "./columnFilter.filterFns";
import type {
  ColumnFilterRangeValue,
  ColumnFilterSpec,
  ColumnFilterValue,
} from "./columnFilter.types";
import { Button } from "@/components/ui/atoms/Button";

export interface AppliedFiltersBarLabels {
  /** "Clear all" control label. */
  clearAll: string;
  /** aria-label for an individual remove (e.g. "Remove {{label}} filter"). */
  removeAria: string;
}

export interface AppliedFiltersBarProps {
  columnFilters: ColumnFiltersState;
  /** Spec per columnId — same specs attached to column `meta.filter`. */
  specs: Record<string, ColumnFilterSpec>;
  onChange: (next: ColumnFiltersState) => void;
  labels: AppliedFiltersBarLabels;
  className?: string;
}

/** Human-readable value summary for a pill (option labels, range readout, raw text). */
function formatFilterValue(
  spec: ColumnFilterSpec,
  value: ColumnFilterValue
): string {
  switch (spec.type) {
    case "text":
      return String(value);
    case "multiSelect":
    case "enum": {
      const selected = (value as string[]) ?? [];
      const labelFor = (v: string) =>
        spec.options?.find((o) => o.value === v)?.label ?? v;
      return selected.map(labelFor).join(", ");
    }
    case "range": {
      const r = value as ColumnFilterRangeValue;
      const unit = spec.unit ? ` ${spec.unit}` : "";
      return `${r.min}–${r.max}${unit}`;
    }
    default:
      return "";
  }
}

export const AppliedFiltersBar: React.FC<AppliedFiltersBarProps> = ({
  columnFilters,
  specs,
  onChange,
  labels,
  className,
}) => {
  const activePills = useMemo(() => {
    return columnFilters
      .map((f) => {
        const spec = specs[f.id];
        if (!spec) return null;
        const value = f.value as ColumnFilterValue;
        if (isColumnFilterEmpty(spec, value)) return null;
        return {
          id: f.id,
          label: spec.label,
          summary: formatFilterValue(spec, value),
        };
      })
      .filter((p): p is { id: string; label: string; summary: string } =>
        Boolean(p)
      );
  }, [columnFilters, specs]);

  if (activePills.length === 0) return null;

  const removeOne = (id: string) =>
    onChange(columnFilters.filter((f) => f.id !== id));
  const clearAll = () => onChange([]);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
      {activePills.map((pill) => (
        <span
          key={pill.id}
          className="inline-flex items-center gap-1.5 rounded-full border border-light-lila/60 bg-light-purple/40 py-1 pl-2.5 pr-1 text-xs font-body text-dark-invirtus"
        >
          <span className="font-medium text-dark-invirtus/70">{pill.label}:</span>
          <span className="max-w-[16rem] truncate">{pill.summary}</span>
          <button
            type="button"
            aria-label={labels.removeAria.replace("{{label}}", pill.label)}
            onClick={() => removeOne(pill.id)}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-dark-invirtus/50 hover:bg-dark-invirtus-soft hover:text-dark-invirtus transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <Button
        type="text"
        intent="muted"
        size="xs"
        onClick={clearAll}
        label={labels.clearAll}
      />
    </div>
  );
};

AppliedFiltersBar.displayName = "AppliedFiltersBar";
